/**
 * Copyright Zendesk, Inc.
 *
 * Use of this source code is governed under the Apache License, Version 2.0
 * found at http://www.apache.org/licenses/LICENSE-2.0.
 */

import * as React from 'react';
import React__default, { cloneElement, Children, createContext, useContext, forwardRef, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import styled, { css } from 'styled-components';
import { retrieveComponentStyles, DEFAULT_THEME, getColor, getLineHeight } from '@zendeskgarden/react-theming';
import { math } from 'polished';
import PropTypes from 'prop-types';
import { useCombinedRefs, composeEventHandlers } from '@zendeskgarden/container-utilities';
import debounce from 'lodash.debounce';
import { useAccordion } from '@zendeskgarden/container-accordion';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends$3() {
  _extends$3 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$3.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var COMPONENT_ID$k = 'accordions.step_content';
var sizeStyles$1 = function sizeStyles(props) {
  var _props$theme = props.theme,
      rtl = _props$theme.rtl,
      space = _props$theme.space;
  var paddingBottom = props.isActive ? space.base * 8 : space.base * 6;
  var paddingRight = rtl ? space.base * 6 : space.base * 5;
  var paddingLeft = rtl ? space.base * 5 : space.base * 6;
  var marginRight = rtl ? space.base * 3 : '0';
  var marginLeft = rtl ? '0' : space.base * 3;
  var marginVertical = space.base * 3;
  return css(["margin:", "px ", "px ", "px ", "px;padding:0 ", "px ", "px ", "px;"], marginVertical, marginRight, marginVertical, marginLeft, paddingRight, paddingBottom, paddingLeft);
};
var StyledContent = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$k,
  'data-garden-version': '8.39.1'
}).withConfig({
  displayName: "StyledContent",
  componentId: "sc-mazvvo-0"
})(["", " min-width:", "px;word-break:break-word;", ";"], sizeStyles$1, function (props) {
  return props.theme.space.base * 30;
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$k, props);
});
StyledContent.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$j = 'accordions.step_line';
var StyledLine = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$j,
  'data-garden-version': '8.39.1'
}).withConfig({
  displayName: "StyledLine",
  componentId: "sc-1gkpjbr-0"
})(["display:block;position:absolute;top:", "px;right:", ";left:", ";flex:1;border-top:", ";border-color:", ";"], function (props) {
  return props.theme.space.base * 3;
}, function (props) {
  return "calc(50% + ".concat(props.theme.space.base * 6, "px)");
}, function (props) {
  return "calc(-50% + ".concat(props.theme.space.base * 6, "px)");
}, function (props) {
  return props.theme.borders.sm;
}, function (props) {
  return getColor('neutralHue', 300, props.theme);
});
StyledLine.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$i = 'accordions.step';
var StyledStep = styled.li.attrs({
  'data-garden-id': COMPONENT_ID$i,
  'data-garden-version': '8.39.1'
}).withConfig({
  displayName: "StyledStep",
  componentId: "sc-12fiwtz-0"
})(["position:", ";flex:", ";min-width:", ";&:last-of-type ", "{display:", ";}&:first-of-type ", "{display:", ";}&:not(:last-of-type) ", "{border-", ":", ";border-color:", ";}", ";"], function (props) {
  return props.isHorizontal && 'relative';
}, function (props) {
  return props.isHorizontal && '1';
}, function (props) {
  return props.isHorizontal && "".concat(props.theme.space.base * 15, "px");
}, StyledLine, function (props) {
  return props.theme.rtl && 'none';
}, StyledLine, function (props) {
  return !props.theme.rtl && 'none';
}, StyledContent, function (props) {
  return props.theme.rtl ? 'right' : 'left';
}, function (props) {
  return props.theme.borders.sm;
}, function (props) {
  return getColor('neutralHue', 300, props.theme);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$i, props);
});
StyledStep.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$h = 'accordions.step_inner_content';
var StyledInnerContent = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$h,
  'data-garden-version': '8.39.1'
}).withConfig({
  displayName: "StyledInnerContent",
  componentId: "sc-1xs9fh7-0"
})(["transition:max-height 0.25s ease-in-out;overflow:hidden;max-height:", ";line-height:", ";color:", ";font-size:", ";", ";"], function (props) {
  return !props.isActive && '0 !important';
}, function (props) {
  return getLineHeight(props.theme.space.base * 5, props.theme.fontSizes.md);
}, function (props) {
  return props.theme.colors.foreground;
}, function (props) {
  return props.theme.fontSizes.md;
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$h, props);
});
StyledInnerContent.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$g = 'accordions.stepper';
var StyledStepper = styled.ol.attrs({
  'data-garden-id': COMPONENT_ID$g,
  'data-garden-version': '8.39.1'
}).withConfig({
  displayName: "StyledStepper",
  componentId: "sc-dsxw0f-0"
})(["display:", ";margin:0;padding:0;list-style:none;", ";"], function (props) {
  return props.isHorizontal && 'flex';
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$g, props);
});
StyledStepper.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$f = 'accordions.step_icon';
var StyledIconFlexContainer = styled.div.withConfig({
  displayName: "StyledIcon__StyledIconFlexContainer",
  componentId: "sc-v20nz9-0"
})(["display:flex;flex-basis:100%;justify-content:center;width:100%;"]);
var sizeStyles = function sizeStyles(props) {
  var size = "".concat(props.theme.space.base * 6, "px");
  var fontSize = props.theme.fontSizes.sm;
  return css(["margin-bottom:", ";margin-", ":", ";width:", ";min-width:", ";height:", ";min-height:", ";line-height:", ";font-size:", ";"], props.isHorizontal && "".concat(props.theme.space.base * 2, "px"), props.theme.rtl ? 'left' : 'right', !props.isHorizontal && "".concat(props.theme.space.base * 3, "px"), size, size, size, size, getLineHeight(size, fontSize), fontSize);
};
var colorStyles$2 = function colorStyles(props) {
  return css(["background:", ";color:", ";"], props.isActive ? getColor('neutralHue', 600, props.theme) : getColor('neutralHue', 200, props.theme), props.isActive ? props.theme.colors.background : props.theme.colors.foreground);
};
var StyledIcon = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$f,
  'data-garden-version': '8.39.1'
}).withConfig({
  displayName: "StyledIcon",
  componentId: "sc-v20nz9-1"
})(["display:flex;align-items:center;justify-content:center;transition:background 0.25s ease-in-out,color 0.25s ease-in-out;border-radius:100%;", " ", " ", ";"], sizeStyles, colorStyles$2, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$f, props);
});
StyledIconFlexContainer.defaultProps = {
  theme: DEFAULT_THEME
};
StyledIcon.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$e = 'accordions.step_label';
var StyledLabel = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$e,
  'data-garden-version': '8.39.1'
}).withConfig({
  displayName: "StyledLabel",
  componentId: "sc-1o82llj-0"
})(["display:", ";align-items:", ";transition:color 0.25s ease-in-out,font-weight 0.25s ease-in-out;text-align:", ";line-height:", ";color:", ";font-size:", ";font-weight:", ";", ";"], function (props) {
  return !props.isHorizontal && 'flex';
}, function (props) {
  return !props.isHorizontal && 'center';
}, function (props) {
  return props.isHorizontal && 'center';
}, function (props) {
  return getLineHeight(props.theme.space.base * 5, props.theme.fontSizes.md);
}, function (props) {
  return props.isActive ? props.theme.colors.foreground : getColor('neutralHue', 600, props.theme);
}, function (props) {
  return props.theme.fontSizes.md;
}, function (props) {
  return props.isActive && 600;
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$e, props);
});
StyledLabel.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$d = 'accordions.step_label_text';
var StyledLabelText = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$d,
  'data-garden-version': '8.39.1'
}).withConfig({
  displayName: "StyledLabelText",
  componentId: "sc-111m5zo-0"
})(["display:", ";padding:", ";word-wrap:", ";"], function (props) {
  return props.isHidden && 'none';
}, function (props) {
  return props.isHorizontal && "0 ".concat(props.theme.space.base * 3, "px");
}, function (props) {
  return props.isHorizontal && 'break-word';
});
StyledLabelText.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$c = 'accordions.accordion';
var StyledAccordion = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$c,
  'data-garden-version': '8.39.1'
}).withConfig({
  displayName: "StyledAccordion",
  componentId: "sc-niv9ic-0"
})(["", ";"], function (props) {
  return retrieveComponentStyles(COMPONENT_ID$c, props);
});
StyledAccordion.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$b = 'accordions.panel';
var paddingStyles = function paddingStyles(props) {
  var base = props.theme.space.base;
  var paddingTop = base * 2;
  var paddingHorizontal = base * 5;
  var paddingBottom = base * 8;
  if (props.isCompact) {
    paddingTop = base * 2;
    paddingHorizontal = base * 3;
    paddingBottom = base * 4;
  }
  if (props.isExpanded === false) {
    paddingTop = 0;
    paddingBottom = 0;
  }
  return css(["transition:", ";padding:", "px ", "px ", "px;"], props.isAnimated && 'padding 0.25s ease-in-out', paddingTop, paddingHorizontal, paddingBottom);
};
var StyledPanel = styled.section.attrs({
  'data-garden-id': COMPONENT_ID$b,
  'data-garden-version': '8.39.1'
}).withConfig({
  displayName: "StyledPanel",
  componentId: "sc-1piryze-0"
})(["", ";border-bottom:", ";", ";"], paddingStyles, function (props) {
  return "".concat(props.theme.borders.sm, " ").concat(props.isBare ? 'transparent' : getColor('neutralHue', 300, props.theme));
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$b, props);
});
StyledPanel.defaultProps = {
  isAnimated: true,
  theme: DEFAULT_THEME
};

var COMPONENT_ID$a = 'accordions.section';
var StyledSection = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$a,
  'data-garden-version': '8.39.1'
}).withConfig({
  displayName: "StyledSection",
  componentId: "sc-v2t9bd-0"
})(["&:last-child ", "{border:none;}", ";"], StyledPanel, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$a, props);
});
StyledSection.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$9 = 'accordions.header';
var StyledHeader = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$9,
  'data-garden-version': '8.39.1'
}).withConfig({
  displayName: "StyledHeader",
  componentId: "sc-2c6rbr-0"
})(["display:flex;align-items:center;box-shadow:", ";font-size:", ";&:hover{cursor:", ";}", ";"], function (props) {
  return props.isFocused && "".concat(props.theme.shadows.md(getColor('primaryHue', 600, props.theme, 0.35)), " inset");
}, function (props) {
  return props.theme.fontSizes.md;
}, function (props) {
  return (props.isCollapsible || !props.isExpanded) && 'pointer';
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$9, props);
});
StyledHeader.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$8 = 'accordions.button';
var colorStyles$1 = function colorStyles(props) {
  var showColor = props.isCollapsible || !props.isExpanded;
  var color = props.theme.colors.foreground;
  if (showColor && props.isHovered) {
    color = getColor('primaryHue', 600, props.theme);
  }
  return css(["color:", ";&:hover{cursor:", ";color:", ";}"], color, showColor && 'pointer', showColor && color);
};
var StyledButton = styled.button.attrs({
  'data-garden-id': COMPONENT_ID$8,
  'data-garden-version': '8.39.1'
}).withConfig({
  displayName: "StyledButton",
  componentId: "sc-xj3hy7-0"
})(["transition:color 0.1s ease-in-out;outline:none;border:none;background:transparent;padding:", ";width:100%;text-align:", ";line-height:", ";font-family:inherit;font-size:", ";font-weight:", ";", " &::-moz-focus-inner{border:0;}&:hover{cursor:", ";}", ";"], function (props) {
  return props.isCompact ? "".concat(props.theme.space.base * 2, "px ").concat(props.theme.space.base * 3, "px") : "".concat(props.theme.space.base * 5, "px");
}, function (props) {
  return props.theme.rtl ? 'right' : 'left';
}, function (props) {
  return getLineHeight(props.theme.space.base * 5, props.theme.fontSizes.md);
}, function (props) {
  return props.theme.fontSizes.md;
}, function (props) {
  return props.theme.fontWeights.semibold;
}, colorStyles$1, function (props) {
  return (props.isCollapsible || !props.isExpanded) && 'pointer';
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$8, props);
});
StyledButton.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$7 = 'accordions.step_inner_panel';
var StyledInnerPanel = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$7,
  'data-garden-version': '8.39.1'
}).withConfig({
  displayName: "StyledInnerPanel",
  componentId: "sc-8nbueg-0"
})(["transition:", ";max-height:", ";overflow:hidden;line-height:", ";font-size:", ";", ";"], function (props) {
  return props.isAnimated && 'max-height 0.25s ease-in-out';
}, function (props) {
  return !props.isExpanded && '0 !important';
}, function (props) {
  return getLineHeight(props.theme.space.base * 5, props.theme.fontSizes.md);
}, function (props) {
  return props.theme.fontSizes.md;
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$7, props);
});
StyledInnerPanel.defaultProps = {
  isAnimated: true,
  theme: DEFAULT_THEME
};

var _excluded$6 = ["children", "isRotated", "isHovered", "isCompact", "isCollapsible"];
var COMPONENT_ID$6 = 'accordions.rotate_icon';
var colorStyles = function colorStyles(props) {
  var showColor = props.isCollapsible || !props.isRotated;
  var color = getColor('neutralHue', 600, props.theme);
  if (showColor && props.isHovered) {
    color = getColor('primaryHue', 600, props.theme);
  }
  return css(["color:", ";&:hover{color:", ";}"], color, showColor && color);
};
var StyledRotateIcon = styled(
function (_ref) {
  var children = _ref.children;
      _ref.isRotated;
      _ref.isHovered;
      _ref.isCompact;
      _ref.isCollapsible;
      var props = _objectWithoutProperties(_ref, _excluded$6);
  return cloneElement(Children.only(children), props);
}).attrs({
  'data-garden-id': COMPONENT_ID$6,
  'data-garden-version': '8.39.1'
}).withConfig({
  displayName: "StyledRotateIcon",
  componentId: "sc-hp435q-0"
})(["transform:", ";transition:transform 0.25s ease-in-out,color 0.1s ease-in-out;box-sizing:content-box;padding:", ";width:", ";height:", ";vertical-align:middle;", " ", ";"], function (props) {
  return props.isRotated && "rotate(".concat(props.theme.rtl ? '-' : '+', "180deg)");
}, function (props) {
  return props.isCompact ? "".concat(props.theme.space.base * 1.5, "px ").concat(props.theme.space.base * 3, "px") : "".concat(props.theme.space.base * 5, "px");
}, function (props) {
  return props.theme.iconSizes.md;
}, function (props) {
  return props.theme.iconSizes.md;
}, colorStyles, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$6, props);
});
StyledRotateIcon.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$5 = 'timeline';
var StyledTimeline = styled.ol.attrs({
  'data-garden-id': COMPONENT_ID$5,
  'data-garden-version': '8.39.1'
}).withConfig({
  displayName: "StyledTimeline",
  componentId: "sc-pig5kv-0"
})(["margin:0;padding:0;list-style:none;", ";"], function (props) {
  return retrieveComponentStyles(COMPONENT_ID$5, props);
});
StyledTimeline.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$4 = 'timeline.content.separator';
var StyledSeparator = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$4,
  'data-garden-version': '8.39.1'
}).withConfig({
  displayName: "StyledSeparator",
  componentId: "sc-fki51e-0"
})(["display:flex;position:relative;justify-content:center;padding:", ";&::after{position:absolute;border-left:", ";height:100%;content:'';}", ";"], function (props) {
  return "".concat(props.theme.space.base * 5, "px ").concat(props.theme.space.base, "px");
}, function (props) {
  return "".concat(props.theme.borders.sm, " ").concat(getColor('neutralHue', 600, props.theme));
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$4, props);
});
StyledSeparator.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$3 = 'timeline.content';
var StyledTimelineContent = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$3,
  'data-garden-version': '8.39.1'
}).withConfig({
  displayName: "StyledContent__StyledTimelineContent",
  componentId: "sc-19phgu1-0"
})(["flex:1;padding:", ";", ";"], function (props) {
  return "".concat(props.theme.space.base * 5, "px ").concat(props.theme.space.base * 4, "px");
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$3, props);
});
StyledTimelineContent.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$2 = 'timeline.opposite.content';
var StyledOppositeContent = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$2,
  'data-garden-version': '8.39.1'
}).withConfig({
  displayName: "StyledOppositeContent",
  componentId: "sc-jurh2k-0"
})(["flex:1;padding:", ";text-align:", ";", ";"], function (props) {
  return "".concat(props.theme.space.base * 5, "px ").concat(props.theme.space.base * 4, "px");
}, function (props) {
  return props.theme.rtl ? 'left' : 'right';
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$2, props);
});
StyledOppositeContent.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$1 = 'timeline.item';
var StyledTimelineItem = styled.li.attrs({
  'data-garden-id': COMPONENT_ID$1,
  'data-garden-version': '8.39.1'
}).withConfig({
  displayName: "StyledItem__StyledTimelineItem",
  componentId: "sc-5mcnzm-0"
})(["display:flex;position:relative;&:last-of-type ", "::after{display:none;}", " ", " ", ";"], StyledSeparator, function (props) {
  return !props.hasOppositeContent && props.isAlternate && css(["&:before{flex:1;content:'';padding:", "px;}"], props.theme.space.base * 4);
}, function (props) {
  return props.isAlternate && css(["&:nth-child(even){flex-direction:row-reverse;", "{text-align:", ";}", "{text-align:", ";}}"], StyledOppositeContent, props.theme.rtl ? 'right' : 'left', StyledTimelineContent, props.theme.rtl ? 'left' : 'right');
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$1, props);
});
StyledTimelineItem.defaultProps = {
  theme: DEFAULT_THEME
};

var _excluded$5 = ["surfaceColor", "children"];
var COMPONENT_ID = 'timeline.icon';
var StyledItemIcon = styled(function (_ref) {
  _ref.surfaceColor;
      var children = _ref.children,
      props = _objectWithoutProperties(_ref, _excluded$5);
  return cloneElement(Children.only(children), props);
}).attrs({
  'data-garden-id': COMPONENT_ID,
  'data-garden-version': '8.39.1'
}).withConfig({
  displayName: "StyledItemIcon",
  componentId: "sc-vz2l6e-0"
})(["z-index:1;box-sizing:content-box;background-color:", ";padding:", "px 0;width:", ";height:", ";color:", ";", ";"], function (props) {
  return props.surfaceColor || props.theme.colors.background;
}, function (props) {
  return props.theme.space.base;
}, function (props) {
  return math("".concat(props.theme.iconSizes.sm, " + 1"));
}, function (props) {
  return math("".concat(props.theme.iconSizes.sm, " + 1"));
}, function (props) {
  return getColor('neutralHue', 600, props.theme);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID, props);
});
StyledItemIcon.defaultProps = {
  theme: DEFAULT_THEME
};

var StepperContext = createContext(undefined);
var useStepperContext = function useStepperContext() {
  var context = useContext(StepperContext);
  if (context === undefined) {
    throw new Error('This component must be rendered within a Stepper component');
  }
  return context;
};

var StepContext = createContext(undefined);
var useStepContext = function useStepContext() {
  var context = useContext(StepContext);
  if (context === undefined) {
    throw new Error('This component must be rendered within a Stepper.Step component');
  }
  return context;
};

var AccordionContext = createContext(undefined);
var useAccordionContext = function useAccordionContext() {
  var context = useContext(AccordionContext);
  if (context === undefined) {
    throw new Error('This component must be rendered within a Accordion component');
  }
  return context;
};

var SectionContext = createContext(undefined);
var useSectionContext = function useSectionContext() {
  var context = useContext(SectionContext);
  if (context === undefined) {
    throw new Error('This component must be rendered within a Accordion.Section component');
  }
  return context;
};

var HeaderContext = createContext(undefined);
var useHeaderContext = function useHeaderContext() {
  var context = useContext(HeaderContext);
  if (context === undefined) {
    throw new Error('This component must be rendered within a Accordion.Header component');
  }
  return context;
};

var TimelineContext = createContext(undefined);
var useTimelineContext = function useTimelineContext() {
  var context = useContext(TimelineContext);
  if (context === undefined) {
    throw new Error('This component must be rendered within a Timeline component');
  }
  return context;
};

var TimelineItemContext = createContext(undefined);
var useTimelineItemContext = function useTimelineItemContext() {
  var context = useContext(TimelineItemContext);
  if (context === undefined) {
    throw new Error('This component must be rendered within a Timeline.Item component');
  }
  return context;
};

var Step = forwardRef(function (props, ref) {
  var _useStepperContext = useStepperContext(),
      currentIndexRef = _useStepperContext.currentIndexRef,
      isHorizontal = _useStepperContext.isHorizontal;
  var _useState = useState(currentIndexRef.current),
      _useState2 = _slicedToArray(_useState, 2),
      currentStepIndex = _useState2[0],
      setIndex = _useState2[1];
  useEffect(function () {
    setIndex(currentIndexRef.current);
    currentIndexRef.current++;
    var currentIndex = currentIndexRef;
    return function () {
      currentIndex.current--;
    };
  }, [currentIndexRef]);
  var value = useMemo(function () {
    return {
      currentStepIndex: currentStepIndex
    };
  }, [currentStepIndex]);
  return React__default.createElement(StepContext.Provider, {
    value: value
  }, React__default.createElement(StyledStep, _extends$3({
    ref: ref,
    isHorizontal: isHorizontal
  }, props), isHorizontal && React__default.createElement(StyledLine, null), props.children));
});
Step.displayName = 'Step';

function _extends$2() { _extends$2 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2.apply(this, arguments); }

var _ref$2 = /*#__PURE__*/React.createElement("path", {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  strokeWidth: 1.25,
  d: "M3 9l3 3 7-7"
});

function SvgCheckSmStroke(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref$2);
}

var Label$1 = forwardRef(function (props, ref) {
  var _useStepContext = useStepContext(),
      currentStepIndex = _useStepContext.currentStepIndex;
  var _useStepperContext = useStepperContext(),
      activeIndex = _useStepperContext.activeIndex,
      isHorizontal = _useStepperContext.isHorizontal;
  var numericStep = currentStepIndex + 1;
  var stepIcon = props.icon || numericStep;
  var isActive = activeIndex === currentStepIndex;
  var isCompleted = activeIndex > currentStepIndex;
  var styledIcon = React__default.createElement(StyledIcon, {
    isActive: isActive,
    isHorizontal: isHorizontal
  }, isCompleted ? React__default.createElement(SvgCheckSmStroke, null) : stepIcon);
  return React__default.createElement(StyledLabel, _extends$3({
    ref: ref,
    isActive: isActive,
    isHorizontal: isHorizontal
  }, props), isHorizontal ? React__default.createElement(StyledIconFlexContainer, null, styledIcon) : styledIcon, React__default.createElement(StyledLabelText, {
    isHidden: props.isHidden,
    isHorizontal: isHorizontal
  }, props.children));
});
Label$1.displayName = 'Label';
Label$1.propTypes = {
  icon: PropTypes.node,
  isHidden: PropTypes.bool
};

var Content$1 = forwardRef(function (props, ref) {
  var contentRef = useCombinedRefs(ref);
  var _useStepperContext = useStepperContext(),
      activeIndex = _useStepperContext.activeIndex,
      isHorizontal = _useStepperContext.isHorizontal;
  var _useStepContext = useStepContext(),
      currentStepIndex = _useStepContext.currentStepIndex;
  var isActive = currentStepIndex === activeIndex;
  var updateMaxHeight = useCallback(debounce(function () {
    if (contentRef.current) {
      var child = contentRef.current.children[0];
      child.style.maxHeight = "".concat(child.scrollHeight, "px");
    }
  }, 100), [contentRef]);
  useEffect(function () {
    if (isActive && isHorizontal === false) {
      addEventListener('resize', updateMaxHeight);
      updateMaxHeight();
      return function () {
        removeEventListener('resize', updateMaxHeight);
      };
    }
    return undefined;
  }, [isActive, isHorizontal, props.children, updateMaxHeight]);
  return isHorizontal === false ? React__default.createElement(StyledContent, _extends$3({
    ref: contentRef,
    isActive: isActive
  }, props), React__default.createElement(StyledInnerContent, {
    isActive: isActive
  }, props.children)) : null;
});
Content$1.displayName = 'Content';

var _excluded$4 = ["isHorizontal", "activeIndex"];
var Stepper = forwardRef(function (_ref, ref) {
  var isHorizontal = _ref.isHorizontal,
      activeIndex = _ref.activeIndex,
      props = _objectWithoutProperties(_ref, _excluded$4);
  var currentIndexRef = useRef(0);
  var stepperContext = useMemo(function () {
    return {
      isHorizontal: isHorizontal,
      activeIndex: activeIndex,
      currentIndexRef: currentIndexRef
    };
  }, [isHorizontal, activeIndex, currentIndexRef]);
  useEffect(function () {
    currentIndexRef.current = 0;
  });
  return React__default.createElement(StepperContext.Provider, {
    value: stepperContext
  }, React__default.createElement(StyledStepper, _extends$3({
    ref: ref,
    isHorizontal: isHorizontal
  }, props)));
});
Stepper.Step = Step;
Stepper.Label = Label$1;
Stepper.Content = Content$1;
Stepper.displayName = 'Stepper';
Stepper.defaultProps = {
  activeIndex: 0,
  isHorizontal: false
};

var Section = forwardRef(function (props, ref) {
  var _useAccordionContext = useAccordionContext(),
      currentIndexRef = _useAccordionContext.currentIndexRef;
  var sectionIndexRef = useRef(currentIndexRef.current++);
  var sectionIndex = sectionIndexRef.current;
  return React__default.createElement(SectionContext.Provider, {
    value: sectionIndex
  }, React__default.createElement(StyledSection, _extends$3({
    ref: ref
  }, props)));
});
Section.displayName = 'Section';

function _extends$1() { _extends$1 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1.apply(this, arguments); }

var _ref$1 = /*#__PURE__*/React.createElement("path", {
  fill: "currentColor",
  d: "M12.688 5.61a.5.5 0 01.69.718l-.066.062-5 4a.5.5 0 01-.542.054l-.082-.054-5-4a.5.5 0 01.55-.83l.074.05L8 9.359l4.688-3.75z"
});

function SvgChevronDownStroke(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref$1);
}

var _excluded$3 = ["onClick", "onFocus", "onBlur", "onMouseOver", "onMouseOut", "children"],
    _excluded2 = ["onClick", "onKeyDown"];
var Header = forwardRef(function (props, ref) {
  var _useAccordionContext = useAccordionContext(),
      ariaLevel = _useAccordionContext.level,
      isCompact = _useAccordionContext.isCompact,
      isCollapsible = _useAccordionContext.isCollapsible,
      getHeaderProps = _useAccordionContext.getHeaderProps,
      getTriggerProps = _useAccordionContext.getTriggerProps,
      expandedSections = _useAccordionContext.expandedSections;
  var onClick = props.onClick,
      onFocus = props.onFocus,
      onBlur = props.onBlur,
      onMouseOver = props.onMouseOver,
      onMouseOut = props.onMouseOut,
      children = props.children,
      other = _objectWithoutProperties(props, _excluded$3);
  var sectionIndex = useSectionContext();
  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isFocused = _useState2[0],
      setIsFocused = _useState2[1];
  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isHovered = _useState4[0],
      setIsHovered = _useState4[1];
  var isExpanded = expandedSections.includes(sectionIndex);
  var _getTriggerProps = getTriggerProps({
    type: 'button',
    index: sectionIndex
  }),
      onTriggerClick = _getTriggerProps.onClick;
      _getTriggerProps.onKeyDown;
      var otherTriggerProps = _objectWithoutProperties(_getTriggerProps, _excluded2);
  var onHeaderFocus = function onHeaderFocus(e) {
    e.persist();
    setTimeout(function () {
      var isAccordionButton = e.target.getAttribute('data-garden-id') === COMPONENT_ID$8;
      var isFocusVisible = e.target.getAttribute('data-garden-focus-visible');
      if (isAccordionButton && isFocusVisible) {
        setIsFocused(true);
      }
    }, 0);
  };
  var value = useMemo(function () {
    return {
      isHovered: isHovered,
      otherTriggerProps: otherTriggerProps
    };
  }, [isHovered, otherTriggerProps]);
  return React__default.createElement(HeaderContext.Provider, {
    value: value
  }, React__default.createElement(StyledHeader, getHeaderProps(_objectSpread2({
    ref: ref,
    ariaLevel: ariaLevel,
    isCompact: isCompact,
    isFocused: isFocused,
    isExpanded: isExpanded,
    isCollapsible: isCollapsible,
    onClick: composeEventHandlers(onClick, onTriggerClick),
    onFocus: composeEventHandlers(onFocus, onHeaderFocus),
    onBlur: composeEventHandlers(onBlur, function () {
      return setIsFocused(false);
    }),
    onMouseOver: composeEventHandlers(onMouseOver, function () {
      return setIsHovered(true);
    }),
    onMouseOut: composeEventHandlers(onMouseOut, function () {
      return setIsHovered(false);
    })
  }, other)), children, React__default.createElement(StyledRotateIcon, {
    isCompact: isCompact,
    isHovered: isHovered,
    isRotated: isExpanded,
    isCollapsible: isCollapsible,
    onMouseOver: composeEventHandlers(onMouseOver, function () {
      return setIsHovered(true);
    }),
    onMouseOut: composeEventHandlers(onMouseOut, function () {
      return setIsHovered(false);
    })
  }, React__default.createElement(SvgChevronDownStroke, null))));
});
Header.displayName = 'Header';

var Label = forwardRef(function (props, ref) {
  var sectionIndex = useSectionContext();
  var _useAccordionContext = useAccordionContext(),
      isCompact = _useAccordionContext.isCompact,
      isCollapsible = _useAccordionContext.isCollapsible,
      expandedSections = _useAccordionContext.expandedSections;
  var isExpanded = expandedSections.includes(sectionIndex);
  var _useHeaderContext = useHeaderContext(),
      isHovered = _useHeaderContext.isHovered,
      otherTriggerProps = _useHeaderContext.otherTriggerProps;
  return React__default.createElement(StyledButton, _extends$3({
    ref: ref,
    isCompact: isCompact,
    isHovered: isHovered,
    isExpanded: isExpanded,
    isCollapsible: isCollapsible
  }, otherTriggerProps, props));
});
Label.displayName = 'Label';

var Panel = forwardRef(function (props, ref) {
  var _useAccordionContext = useAccordionContext(),
      isCompact = _useAccordionContext.isCompact,
      isBare = _useAccordionContext.isBare,
      isAnimated = _useAccordionContext.isAnimated,
      getPanelProps = _useAccordionContext.getPanelProps,
      expandedSections = _useAccordionContext.expandedSections;
  var panelRef = useCombinedRefs(ref);
  var index = useSectionContext();
  var isExpanded = expandedSections.includes(index);
  var updateMaxHeight = useCallback(debounce(function () {
    if (panelRef.current) {
      var child = panelRef.current.children[0];
      child.style.maxHeight = "".concat(child.scrollHeight, "px");
    }
  }, 100), [panelRef]);
  React__default.useEffect(function () {
    if (!isAnimated) {
      return undefined;
    }
    addEventListener('resize', updateMaxHeight);
    updateMaxHeight();
    return function () {
      removeEventListener('resize', updateMaxHeight);
    };
  }, [isAnimated, isExpanded, updateMaxHeight, props.children]);
  return React__default.createElement(StyledPanel, getPanelProps(_objectSpread2({
    role: null,
    ref: panelRef,
    index: index,
    isBare: isBare,
    isCompact: isCompact,
    isExpanded: isExpanded,
    isAnimated: isAnimated
  }, props)), React__default.createElement(StyledInnerPanel, {
    isExpanded: isExpanded,
    isAnimated: isAnimated
  }, props.children));
});
Panel.displayName = 'Panel';

var _excluded$2 = ["level", "isBare", "onChange", "isCompact", "isAnimated", "isExpandable", "isCollapsible", "expandedSections"];
var Accordion = forwardRef(function (_ref, ref) {
  var level = _ref.level,
      isBare = _ref.isBare,
      onChange = _ref.onChange,
      isCompact = _ref.isCompact,
      isAnimated = _ref.isAnimated,
      isExpandable = _ref.isExpandable,
      isCollapsible = _ref.isCollapsible,
      controlledExpandedSections = _ref.expandedSections,
      props = _objectWithoutProperties(_ref, _excluded$2);
  var _useAccordion = useAccordion({
    collapsible: isCollapsible,
    expandable: isExpandable,
    onChange: onChange,
    expandedSections: controlledExpandedSections
  }),
      expandedSections = _useAccordion.expandedSections,
      getHeaderProps = _useAccordion.getHeaderProps,
      getTriggerProps = _useAccordion.getTriggerProps,
      getPanelProps = _useAccordion.getPanelProps;
  var currentIndexRef = useRef(0);
  useEffect(function () {
    currentIndexRef.current = 0;
  });
  var value = useMemo(function () {
    return {
      level: level,
      isBare: isBare,
      isCompact: isCompact,
      isAnimated: isAnimated,
      isCollapsible: isCollapsible,
      getPanelProps: getPanelProps,
      getHeaderProps: getHeaderProps,
      getTriggerProps: getTriggerProps,
      currentIndexRef: currentIndexRef,
      expandedSections: expandedSections
    };
  }, [level, isBare, isCompact, isAnimated, isCollapsible, getPanelProps, getHeaderProps, getTriggerProps, currentIndexRef, expandedSections]);
  return React__default.createElement(AccordionContext.Provider, {
    value: value
  }, React__default.createElement(StyledAccordion, _extends$3({
    ref: ref
  }, props)));
});
Accordion.Section = Section;
Accordion.Header = Header;
Accordion.Label = Label;
Accordion.Panel = Panel;
Accordion.displayName = 'Accordion';
Accordion.defaultProps = {
  isBare: false,
  isCompact: false,
  isAnimated: true,
  isCollapsible: true,
  isExpandable: false,
  expandedSections: undefined,
  onChange: function onChange() {
    return undefined;
  }
};

var _excluded$1 = ["icon", "surfaceColor"];
var Item = forwardRef(function (_ref, ref) {
  var icon = _ref.icon,
      surfaceColor = _ref.surfaceColor,
      props = _objectWithoutProperties(_ref, _excluded$1);
  var value = useMemo(function () {
    return {
      icon: icon,
      surfaceColor: surfaceColor
    };
  }, [icon, surfaceColor]);
  var _useTimelineContext = useTimelineContext(),
      isAlternate = _useTimelineContext.isAlternate;
  var hasOppositeContent = false;
  Children.forEach(props.children, function (child) {
    if (child) {
      if (child.type === Timeline.OppositeContent) {
        hasOppositeContent = true;
      }
    }
  });
  return React__default.createElement(TimelineItemContext.Provider, {
    value: value
  }, React__default.createElement(StyledTimelineItem, _extends$3({
    ref: ref,
    isAlternate: isAlternate,
    hasOppositeContent: hasOppositeContent
  }, props)));
});
Item.displayName = 'Item';

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

var _ref = /*#__PURE__*/React.createElement("circle", {
  cx: 6,
  cy: 6,
  r: 4.5,
  fill: "none",
  stroke: "currentColor"
});

function SvgCircleFullStroke(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    xmlns: "http://www.w3.org/2000/svg",
    width: 12,
    height: 12,
    viewBox: "0 0 12 12",
    focusable: "false",
    role: "presentation"
  }, props), _ref);
}

var Content = forwardRef(function (props, ref) {
  var _useTimelineItemConte = useTimelineItemContext(),
      icon = _useTimelineItemConte.icon,
      surfaceColor = _useTimelineItemConte.surfaceColor;
  return React__default.createElement(React__default.Fragment, null, React__default.createElement(StyledSeparator, null, React__default.createElement(StyledItemIcon, {
    surfaceColor: surfaceColor
  }, icon || React__default.createElement(SvgCircleFullStroke, null))), React__default.createElement(StyledTimelineContent, _extends$3({
    ref: ref
  }, props)));
});
Content.displayName = 'Content';

var OppositeContent = forwardRef(function (props, ref) {
  return React__default.createElement(StyledOppositeContent, _extends$3({
    ref: ref
  }, props));
});
OppositeContent.displayName = 'OppositeContent';

var _excluded = ["isAlternate"];
var Timeline = forwardRef(function (_ref, ref) {
  var isAlternate = _ref.isAlternate,
      props = _objectWithoutProperties(_ref, _excluded);
  var value = useMemo(function () {
    return {
      isAlternate: isAlternate
    };
  }, [isAlternate]);
  return React__default.createElement(TimelineContext.Provider, {
    value: value
  }, React__default.createElement(StyledTimeline, _extends$3({
    ref: ref
  }, props)));
});
Timeline.Item = Item;
Timeline.Content = Content;
Timeline.OppositeContent = OppositeContent;
Timeline.displayName = 'Timeline';

export { Accordion, Stepper, Timeline };
